import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ca } from '../i18n/ca';

/** A single answer the dialog collects, when it asks for one. */
export interface ConfirmField {
  label: string;
  /**
   * Shown as typed. The one field this has today is a password an admin sets
   * and then hands over in person, so hiding it from the person who has to
   * read it out would be theatre; `window.prompt` showed it too.
   */
  placeholder?: string;
  /** The confirming button stays disabled until the answer is this long. */
  minLength?: number;
  /** Said under the field, always: what makes an answer acceptable. */
  hint?: string;
}

/** What to ask, and what the two buttons say. */
export interface ConfirmRequest {
  /** The question itself, short: "Esborrar «Nit sobre Kavala»?" */
  title: string;
  /** What happens if they say yes. One or two sentences. */
  body?: ReactNode;
  /** The confirming button's label; a verb, never "OK". */
  confirmLabel: string;
  /** Defaults to "Cancel·la". */
  cancelLabel?: string;
  /** Paints the confirming button as destructive. */
  danger?: boolean;
  /** Ask for a value rather than a yes: the dialog answers with what was typed. */
  field?: ConfirmField;
}

/**
 * A confirmation of our own, in the page.
 *
 * `window.confirm` looks close enough for free, and it is not: it is a modal
 * of the browser's, so it cannot say what the platform wants to say, cannot be
 * styled, and — the reason this exists — it **blocks the renderer**. While one
 * is open the page runs nothing, which means every automated check of a path
 * behind a confirmation (and every screenshot of it) hangs until a person
 * clicks. That was found on 2026-09-07 trying to verify the mission delete
 * button against the live site.
 *
 * The dialog is a plain element rather than `<dialog>` because jsdom, which the
 * tests run on (25 then, 30 now), implements neither `showModal` nor `close`;
 * building it by hand is a few lines and keeps the tests honest instead of
 * polyfilled.
 *
 * Used through {@link useConfirm}, which gives back an `await`-able function,
 * so a call site reads almost the way it read with the native one:
 *
 * ```tsx
 * const [confirm, confirmDialog] = useConfirm();
 * // ...
 * if (!(await confirm({ title: ca.x, confirmLabel: ca.y, danger: true }))) return;
 * // ...
 * return (<>{confirmDialog}<button onClick={() => void remove()}>…</button></>);
 * ```
 */
export function ConfirmDialog({
  request,
  onAnswer,
}: {
  request: ConfirmRequest;
  onAnswer: (yes: boolean, value: string) => void;
}) {
  const panel = useRef<HTMLDivElement>(null);
  const confirmButton = useRef<HTMLButtonElement>(null);
  const fieldInput = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState('');
  const short = (request.field?.minLength ?? 0) > value.length;

  // What the reader came for takes the focus: the field when there is one, the
  // answering button otherwise. Escape is "no", the same answer the native
  // dialogs gave, and focus goes back where it was so the list they were
  // working down does not lose their place.
  useEffect(() => {
    const before = document.activeElement as HTMLElement | null;
    (fieldInput.current ?? confirmButton.current)?.focus();
    return () => before?.focus?.();
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onAnswer(false, '');
        return;
      }
      // A modal that lets Tab wander behind it is not modal. Two buttons is a
      // small enough trap to write out.
      if (e.key !== 'Tab' || !panel.current) return;
      const stops = [
        ...panel.current.querySelectorAll<HTMLElement>('button, [href], input, select'),
      ];
      if (stops.length === 0) return;
      const first = stops[0];
      const last = stops[stops.length - 1];
      if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    }
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onAnswer]);

  return (
    <div
      className="confirm-backdrop"
      // Clicking away is "no", like Escape. The panel stops the click so a drag
      // that ends outside does not answer for the reader.
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onAnswer(false, '');
      }}
    >
      <div
        className="confirm-panel"
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
      >
        <h2 id="confirm-title">{request.title}</h2>
        {request.body && <p>{request.body}</p>}
        {request.field && (
          <label>
            {request.field.label}
            <input
              ref={fieldInput}
              name="confirm-field"
              value={value}
              placeholder={request.field.placeholder}
              onChange={(e) => setValue(e.target.value)}
              // Enter answers, the way it did in the browser's own prompt.
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !short) onAnswer(true, value);
              }}
            />
            {request.field.hint && <span className="muted small">{request.field.hint}</span>}
          </label>
        )}
        <div className="row end">
          <button type="button" onClick={() => onAnswer(false, '')}>
            {request.cancelLabel ?? ca.cancel}
          </button>
          <button
            type="button"
            ref={confirmButton}
            className={request.danger ? 'danger' : 'primary'}
            disabled={short}
            onClick={() => onAnswer(true, value)}
          >
            {request.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
